import React, { useState, useEffect, Suspense, lazy, useMemo } from 'react'
import { Box, Chip, Checkbox, Typography, CircularProgress, Grid2, MenuItem } from '@mui/material'
import { GRAY_COLORS, INFO_COLORS } from 'asterX-UI/colors.constant'
import { getValidDate } from 'lib/utils'
import { useHomeStore } from 'context/HomeStore'

import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { enqueueSnackbar } from 'notistack'
import { NotFound } from 'components/NotFound/NotFound'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import { ExpandMore } from '@mui/icons-material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { useResErrors } from 'lib/hooks'
import { format } from 'date-fns'
import ActionModal from '../../../asterX-UI/Modals/ActionModal'
import MaskedTextInput from '../../../asterX-UI/Inputs/MackedTextInput'
import { useContractStore } from '../../../context/ContractStore'


const StandardTable = lazy(() => import('asterX-UI/Table/StandardTable'))
const initPagination = { limit: 100, offset: 0, count: 0 }

const CreateAllDdsModal = ({ open, onClose = () => {} }) => {
  const { standardCheck } = useResErrors()
  const {
    homeStoreFunc: { getContractList }
  } = useHomeStore()
  const {
    contractStore: { loanApplicationData }
  } = useContractStore()

  const [exportListData, setExportListData] = useState([])
  const [pagination, setPagination] = useState(initPagination)
  const [selectedRows, setSelectedRows] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  const [banks, setBanks] = useState([])

  const [ddsData, setDdsData] = useState({
    bankAccount: '',
    date: new Date(),
    time: new Date(),
    paymentPurposeCode: loanApplicationData?.contract?.knp || '',
    type: '',
    reasonType: '',
    invoiceDate: null,
    invoiceNumber: null
  })

  const getBanks = async () => {
    try {
      const response = await CreditFlow.getBanks()
      if (response.data) {
        setBanks(response.data?.list)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getContractListData = async () => {
    const { list, count } = await getContractList(
      { setDDS: false, statusCode: 'userSetDDS' },
      pagination.limit,
      pagination.offset
    )
    setExportListData(list)
    setPagination(prevState => ({ ...prevState, count }))
  }

  useEffect(() => {
    if (open) {
      getContractListData()
      getBanks()
    }
  }, [open])

  useEffect(() => {
    if (selectedRows?.length !== exportListData?.length) {
      setSelectAll(false)
    } else if (exportListData?.length > 0) {
      setSelectAll(true)
    }
  }, [selectedRows, exportListData])

  const resetState = () => {
    onClose()
    setExportListData([])
    setPagination(initPagination)
    setSelectedRows([])
    setSelectAll(false)
    setDdsData({
      bankAccount: '',
      date: new Date(),
      time: new Date(),
      paymentPurposeCode: loanApplicationData?.contract?.knp,
      type: '',
      reasonType: '',
      invoiceDate: null,
      invoiceNumber: null
    })
  }



  const handletDDSChange = e => {
    const { name, value } = e.target
    setDdsData(prevDates => ({
      ...prevDates,
      [name]: value
    }))
  }

  function parseRequisites(requisites) {
    const labels = [
      'Счет получателя',
      'КБЕ',
      'Банк-получатель',
      'Получатель',
      'БИК банка',
      'БИН партнера'
    ];
    const items = (requisites || '').split(',').map(s => s.trim());
    const result = {};
    labels.forEach((label, idx) => {
      result[label] = items[idx] || null;
    });
    return result;
  }

  const handleClose = () => {
    resetState()
  }

  const handleSelectAll = () => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)
    setSelectedRows(
      newSelectAll
        ? exportListData.map(item => ({
          id: item?.loanApplication?.id,
          invoiceNumber: '',
          invoiceDate: null
        }))
        : []
    )
  }

  const handleRowSelect = id => {
    const already = selectedRows.find(row => row.id === id)
    if (already) {
      setSelectedRows(prev => prev.filter(row => row.id !== id))
    } else {
      setSelectedRows(prev => [...prev, { id, invoiceNumber: '', invoiceDate: null }])
    }
  }

  const handleInvoiceFieldChange = (id, field, value) => {
    setSelectedRows(prev =>
      prev.map(row =>
        row.id === id ? { ...row, [field]: value } : row
      )
    )
  }

  const handleConfirm = async () => {
    if (selectedRows.length === 0) {
      enqueueSnackbar('Выберите из списка', { variant: 'info' })
      return
    }

    try {
      const data = {
        ddsDTOList: selectedRows.map(row => {
          const item = exportListData.find(el => el?.loanApplication?.id === row.id)

          return {
            loanApplicationId: row.id,
            documentDate: ddsData.date,
            ddsSum: item?.loanApplication?.loanAmount || 0,
            paymentCode: ddsData.bankAccount?.id,
            paymentPurposeCode: ddsData.paymentPurposeCode,
            bankDataBin: ddsData.bankAccount?.bin,
            bankDataBik: ddsData.bankAccount?.bik,
            bankDataSettlementAccount: ddsData.bankAccount?.settlementAccountId,
            bankDataName: ddsData.bankAccount?.name,
            bankCodeName: banks.find(bank => bank?.name === ddsData.bankAccount?.name)?.codeName || '',
            invoiceNumber: row.invoiceNumber,
            invoiceDate: row.invoiceDate ? format(row.invoiceDate, 'yyyy-MM-dd') : null,
            type: 'expense'
          }
        })
      }

      const res = await CreditFlow.createDDS(data)

      standardCheck(res, () => {
        if (res?.data) {
          resetState()
          enqueueSnackbar('Успешно', { variant: 'success' })
        }
      })
    } catch (error) {
      console.error('Ошибка:', error)
      enqueueSnackbar('Ошибка при выполнении запроса', { variant: 'error' })
    }
  }

  const tableData = {
    columns: [
      <Box display="flex" gap="10px" alignItems="center">
        <Checkbox
          checked={selectAll}
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all' }}
          sx={{ margin: 0, padding: 0 }}
        />
        <Typography>№</Typography>
      </Box>,
      'Клиент',
      'Номер контракта',
      'КНП',
      'Назначения платежа',
      'Получатель',
      'Сумма займа',
      'Дата создания',
      'Счет на оплату',
      'Статус'
    ],
    rows: exportListData?.map(item => {
      const isChecked = selectedRows.some(row => row.id === item?.loanApplication?.id)
      const parsed = item?.requisites ? parseRequisites(item.requisites) : {};
      return [
        <Box display="flex" gap="10px" alignItems="center">
          <Checkbox
            checked={isChecked}
            onChange={() => handleRowSelect(item?.loanApplication?.id)}
            inputProps={{ 'aria-label': `select row ${item?.loanApplication?.id}` }}
            sx={{ margin: 0, padding: 0 }}
          />
          <Typography sx={{ color: INFO_COLORS[700] }}>{item?.id || '-'}</Typography>
        </Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.loanApplication?.clientFullName || '-'}</Box>,
        <Box sx={{ color: INFO_COLORS[700] }}>{item?.bsContractName || '-'}</Box>,
        <Box>{item?.knp || '-'}</Box>,
        <Box>{item?.purposeOfPayment || '-'}</Box>,
        <Box>
          {Object.values(parsed).some(value => !!value) ? (
            Object.entries(parsed).map(([label, value]) =>
                value && (
                  <Typography key={label}>
                    <strong>{label}:</strong> {value}
                  </Typography>
                )
            )
          ) : (
            <Typography>-</Typography>
          )}
        </Box>,
        item?.loanApplication?.loanAmount?.toString()?.thousandSeparator() || '-',
        getValidDate(item?.loanApplication?.createdDate, true),
        <Box display="flex" flexDirection="column" gap="4px">
          <MaskedTextInput
            name="invoiceNumber"
            placeholder="Номер счета"
            value={selectedRows.find(row => row.id === item?.loanApplication?.id)?.invoiceNumber || ''}
            onChange={e => handleInvoiceFieldChange(item?.loanApplication?.id, 'invoiceNumber', e.target.value)}
            fullWidth
          />
          <DatePicker
            value={selectedRows.find(row => row.id === item?.loanApplication?.id)?.invoiceDate || null}
            onChange={date => handleInvoiceFieldChange(item?.loanApplication?.id, 'invoiceDate', date)}
            format="dd.MM.yyyy"
            fullWidth
          />
        </Box>,
        <Chip
          size="small"
          label={item?.loanApplication?.status?.name || item?.loanApplication?.status?.code || 'Заявка авторизована'}
          variant="outlined"
          sx={{ borderRadius: '6px', fontSize: '12px' }}
        />
      ]
    })
  }

  const disableConfirm = useMemo(() => {
    const hasUnfilledInvoices = selectedRows.some(row => !row.invoiceNumber || !row.invoiceDate)
    return !ddsData.date || !ddsData.time || !ddsData.bankAccount || selectedRows.length === 0 || hasUnfilledInvoices
  }, [ddsData, selectedRows])



  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      title="Создать ДДС"
      labelConfirm="Создать"
      maxWidth="xxl"
      fullWidth
      disableConfirm={disableConfirm}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
          marginTop: '5px'
        }}
      >
        {exportListData?.length > 0 ? (
          <>
            <Grid2
              container
              size={{ xs: 12 }}
              spacing={4}
              sx={{ border: `1px solid ${GRAY_COLORS[300]}`, borderRadius: 2, padding: 3 }}
            >
              <Grid2 size={{ xs: 12 }} display="flex" flexDirection="column">
                <Typography variant="small">Банк - расчетный счет</Typography>
                <SelectPlaceholder
                  IconComponent={props => <ExpandMore {...props} />}
                  name="bankAccount"
                  placeholder="Не выбрано"
                  value={ddsData.bankAccount}
                  onChange={handletDDSChange}
                  fullWidth
                >
                  <MenuItem value={''}>Не выбрано</MenuItem>
                  {banks.map((item, index) => (
                    <MenuItem value={item} key={index}>
                      {item?.name}
                    </MenuItem>
                  ))}
                </SelectPlaceholder>
              </Grid2>
              <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                <Typography variant="small">Дата</Typography>
                <DatePicker
                  value={ddsData.date}
                  // onChange={handletDDSDateChange('date')}
                  fullWidth
                  format="dd.MM.yyyy"
                  KeyboardButtonProps={{
                    'aria-label': 'change date'
                  }}
                  disabled={true}
                />
              </Grid2>
              <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                <Typography variant="small">Время</Typography>
                <TimePicker
                  value={ddsData.time}
                  // onChange={handletDDSDateChange('time')}
                  fullWidth
                  ampm={false}
                  KeyboardButtonProps={{
                    'aria-label': 'change time'
                  }}
                  disabled={true}
                />
              </Grid2>
              <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                <Typography variant="small">Бик банка</Typography>
                <MaskedTextInput placeholder="БИК банка" value={ddsData.bankAccount?.bik || ''} fullWidth disabled />
              </Grid2>
              <Grid2 size={{ xs: 6 }} display="flex" flexDirection="column">
                <Typography variant="small">Счет банка </Typography>
                <MaskedTextInput
                  placeholder="Счет банка"
                  value={ddsData.bankAccount?.bankAccountNumber || ''}
                  fullWidth
                  disabled
                />
              </Grid2>
            </Grid2>
            <Suspense
              fallback={
                <Box display="flex" justifyContent="center" alignItems="center">
                  <CircularProgress />
                </Box>
              }
            >
              <StandardTable
                tableData={tableData}
                tableStyles={{ border: `1px solid ${GRAY_COLORS[200]}` }}
                btnStyle={{ padding: 0, border: 'none' }}
              />
            </Suspense>
          </>
        ) : (
          <NotFound title="Заявки для формирования ДДС отсутствуют" fontSize="18px" />
        )}
      </Box>
    </ActionModal>
  )
}

export default CreateAllDdsModal
